import React, { useState, useEffect } from "react";
import { Button, Container, Row, Col, Table, Form, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import axios from "axios";
import { API_BASE_URL } from "config.js";
import { useNavigate } from "react-router-dom";

function PhoneNumbers() {
  const [areaCode, setAreaCode] = useState("");
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [purchasingId, setPurchasingId] = useState(null);
  const [userCredits, setUserCredits] = useState(0);
  const [selectedNumbers, setSelectedNumbers] = useState([]);

  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchUserCredits();
    document.title = "Buy Phone Numbers";
    handleSearch();
  }, []);

  const fetchUserCredits = async () => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("user"));
      const token = localStorage.getItem("token");
      if (storedUser && token) {
        const userId = storedUser.id || storedUser._id;
        const res = await axios.get(`${API_BASE_URL}/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success) {
          setUserCredits(res.data.data.credits || 0);
        }
      }
    } catch (err) {
      console.error("Failed to fetch user credits:", err);
    }
  };

  const handleSearch = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (areaCode && areaCode.length !== 3) {
      return Swal.fire("Invalid Input", "If specified, area code must be 3 digits.", "warning");
    }

    setSearching(true);
    setAvailableNumbers([]);
    setSelectedNumbers([]);

    try {
      const url = areaCode ? `${API_BASE_URL}/phone-numbers/search?areaCode=${areaCode}` : `${API_BASE_URL}/phone-numbers/search`;
      const response = await axios.get(
        url,
        { headers: getAuthHeaders() }
      );
      if (response.data.success) {
        setAvailableNumbers(response.data.data);
        if (response.data.data.length === 0) {
          Swal.fire("No Numbers Found", "No phone numbers available.", "info");
        }
      }
    } catch (error) {
      console.error("Error searching numbers:", error);
      Swal.fire("Search Failed", error.response?.data?.message || "Failed to search available numbers.", "error");
    } finally {
      setSearching(false);
    }
  };

  const handlePurchase = async (num) => {
    const costInCredits = 200; // $2.00
    if (userCredits < costInCredits) {
      return Swal.fire({
        title: "Insufficient Credits",
        text: `Purchasing a number costs $2.00 (200 credits). Your current balance is $${(userCredits / 100).toFixed(2)}. Please add credits to your account first.`,
        icon: "error",
        showCancelButton: true,
        confirmButtonText: "Go to Billing",
        confirmButtonColor: "#004992",
        cancelButtonColor: "#6c757d"
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/admin/billing");
        }
      });
    }

    const confirm = await Swal.fire({
      title: "Purchase Phone Number",
      html: `Would you like to buy <strong>${num.friendlyName || num.phoneNumber}</strong>?<br/><br/>One-time charge: <strong>$2.00 (200 credits)</strong>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Confirm Purchase",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#004992",
      cancelButtonColor: "#6c757d"
    });

    if (!confirm.isConfirmed) return;

    setPurchasingId(num.phoneNumber);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/phone-numbers/purchase`,
        { phoneNumber: num.phoneNumber },
        { headers: getAuthHeaders() }
      );

      if (response.data.success) {
        await Swal.fire("Success!", `Purchased ${num.friendlyName || num.phoneNumber} and added it to your rotation pool.`, "success");
        // Remove purchased number from list
        setAvailableNumbers(prev => prev.filter(item => item.phoneNumber !== num.phoneNumber));
        // Refresh balance
        fetchUserCredits();
      }
    } catch (error) {
      console.error("Purchase failed:", error);
      Swal.fire("Purchase Failed", error.response?.data?.message || "Failed to purchase phone number.", "error");
    } finally {
      setPurchasingId(null);
    }
  };

  const handleToggleSelect = (phoneNumber) => {
    setSelectedNumbers(prev =>
      prev.includes(phoneNumber)
        ? prev.filter(num => num !== phoneNumber)
        : [...prev, phoneNumber]
    );
  };

  const handleToggleSelectAll = () => {
    if (selectedNumbers.length === availableNumbers.length) {
      setSelectedNumbers([]);
    } else {
      setSelectedNumbers(availableNumbers.map(n => n.phoneNumber));
    }
  };

  const handleBulkPurchase = async () => {
    if (selectedNumbers.length === 0) return;
    const singleCost = 200;
    const totalCost = selectedNumbers.length * singleCost;

    if (userCredits < totalCost) {
      return Swal.fire({
        title: "Insufficient Credits",
        text: `Purchasing ${selectedNumbers.length} numbers costs $${(totalCost / 100).toFixed(2)} (${totalCost} credits). Your balance: $${(userCredits / 100).toFixed(2)}. Please add credits.`,
        icon: "error",
        showCancelButton: true,
        confirmButtonText: "Go to Billing",
        confirmButtonColor: "#004992",
        cancelButtonColor: "#6c757d"
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/admin/billing");
        }
      });
    }

    const confirm = await Swal.fire({
      title: "Bulk Purchase Phone Numbers",
      html: `Are you sure you want to buy <strong>${selectedNumbers.length}</strong> selected phone numbers?<br/><br/>Total charge: <strong>$${(totalCost / 100).toFixed(2)} (${totalCost} credits)</strong>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Confirm Purchase",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#004992",
      cancelButtonColor: "#6c757d"
    });

    if (!confirm.isConfirmed) return;

    setPurchasingId("bulk");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/phone-numbers/bulk-purchase`,
        { phoneNumbers: selectedNumbers },
        { headers: getAuthHeaders() }
      );

      if (response.data.success) {
        const { succeeded, failed } = response.data;
        let htmlSummary = `<p>Successfully purchased <strong>${succeeded.length}</strong> numbers.</p>`;
        if (failed.length > 0) {
          htmlSummary += `<p style="color: red;">Failed to purchase ${failed.length} numbers:</p><ul style="text-align: left; max-height: 150px; overflow-y: auto;">`;
          failed.forEach(f => {
            htmlSummary += `<li>${f.phoneNumber}: ${f.error}</li>`;
          });
          htmlSummary += `</ul>`;
        }

        await Swal.fire({
          title: failed.length > 0 ? "Purchase Completed with Warnings" : "Success!",
          html: htmlSummary,
          icon: failed.length > 0 ? "warning" : "success"
        });

        setAvailableNumbers(prev => prev.filter(item => !succeeded.includes(item.phoneNumber)));
        setSelectedNumbers([]);
        fetchUserCredits();
      }
    } catch (error) {
      console.error("Bulk purchase failed:", error);
      Swal.fire("Bulk Purchase Failed", error.response?.data?.message || "Failed to complete bulk purchase.", "error");
    } finally {
      setPurchasingId(null);
    }
  };

  const resetFilters = () => {
    setAreaCode("");
    setAvailableNumbers([]);
    setSelectedNumbers([]);
  };

  return (
    <Container fluid className="p-3" style={{ marginTop: "20px" }}>
      <p
        className="text-muted mb-0"
        style={{ cursor: "pointer", fontWeight: "600" }}
        onClick={() => navigate("/admin/phone-numbers")}
      >
        &larr; Back to Active Numbers
      </p>
      
      <Row>
        <Col lg="12" sm="12">
          <h2 className="fw-bold mb-2 mt-3" style={{ fontWeight: "700" }}>Purchase Phone Numbers</h2>
          <p style={{ color: "#5e5e5e", fontWeight: "600", marginBottom: "20px" }}>
            Search and buy local US phone numbers to add to your messaging rotation.
          </p>

          <div
            className="mb-4"
            style={{
              background: "white",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
              maxWidth: "550px",
              padding: "25px 30px",
            }}
          >
            <Form onSubmit={handleSearch}>
              <Form.Group className="mb-3">
                <Form.Label className="font-weight-bold text-dark">Area Code (Optional)</Form.Label>
                <div className="d-flex align-items-center justify-content-between" style={{ gap: "15px" }}>
                  <Form.Control
                    type="text"
                    maxLength={3}
                    placeholder="e.g. 786"
                    value={areaCode}
                    onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, ""))}
                    style={{
                      maxWidth: "200px",
                      fontWeight: "700",
                      border: "2px solid #c7c7c7",
                      borderRadius: "6px",
                    }}
                  />
                  <Button
                    type="submit"
                    className="fw-bold mb-0 text-white"
                    disabled={searching}
                    style={{
                      backgroundColor: "#004992",
                      padding: "10px 30px",
                      fontWeight: "700",
                      borderRadius: "6px",
                      border: "none",
                    }}
                  >
                    {searching ? <Spinner animation="border" size="sm" /> : "Search"}
                  </Button>
                  <Button
                    variant="link"
                    type="button"
                    className="fw-bold"
                    onClick={resetFilters}
                    style={{ fontWeight: '700', fontSize: ".9rem", color: "#0090ff" }}
                  >
                    Reset
                  </Button>
                </div>
              </Form.Group>
            </Form>
          </div>

          <div
            style={{
              background: "white",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
              padding: "25px 30px",
              marginTop: "20px"
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3 px-2">
              <div className="d-flex align-items-center" style={{ gap: "15px" }}>
                <h5 className="m-0 fw-bold">Available Numbers</h5>
                {selectedNumbers.length > 0 && (
                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleBulkPurchase}
                    disabled={purchasingId !== null}
                    style={{
                      fontWeight: "700",
                      borderRadius: "6px",
                      backgroundColor: "#198754",
                      border: "none",
                      padding: "6px 15px",
                    }}
                  >
                    {purchasingId === "bulk" ? <Spinner animation="border" size="sm" className="me-2" /> : `Buy Selected (${selectedNumbers.length})`}
                  </Button>
                )}
              </div>
              <span className="badge bg-secondary p-2" style={{ fontSize: "0.9rem" }}>
                Your Balance: ${(userCredits / 100).toFixed(2)}
              </span>
            </div>
            
            <Table hover responsive style={{ marginBottom: 0 }}>
              <thead className="table-light" style={{ borderBottom: "2px solid #ddd" }}>
                <tr>
                  <th style={{ width: "40px", paddingLeft: "15px" }}>
                    <div className="d-flex align-items-center justify-content-start" style={{ minHeight: "24px" }}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={availableNumbers.length > 0 && selectedNumbers.length === availableNumbers.length}
                        onChange={handleToggleSelectAll}
                        disabled={availableNumbers.length === 0}
                        style={{ cursor: "pointer", width: "16px", height: "16px", margin: 0 }}
                      />
                    </div>
                  </th>
                  <th style={{ fontWeight: "700" }}>Phone Number</th>
                  <th style={{ fontWeight: "700" }}>Monthly Rent Cost</th>
                  <th className="text-end" style={{ fontWeight: "700", paddingRight: "30px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {searching ? (
                  <tr>
                    <td colSpan="4" className="text-center py-4">
                      <Spinner animation="border" />
                      <div className="mt-2 fw-bold text-muted">Searching Twilio inventory...</div>
                    </td>
                  </tr>
                ) : availableNumbers.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center text-muted py-4 fw-bold">
                      Enter an optional area code above to search for available phone numbers.
                    </td>
                  </tr>
                ) : (
                  availableNumbers.map((num) => (
                     <tr key={num.phoneNumber} style={{ borderBottom: "1px solid #ddd" }}>
                       <td style={{ paddingLeft: "15px", verticalAlign: "middle" }}>
                         <div className="d-flex align-items-center justify-content-start" style={{ minHeight: "24px" }}>
                           <input
                             type="checkbox"
                             className="form-check-input"
                             checked={selectedNumbers.includes(num.phoneNumber)}
                             onChange={() => handleToggleSelect(num.phoneNumber)}
                             style={{ cursor: "pointer", width: "16px", height: "16px", margin: 0 }}
                           />
                         </div>
                       </td>
                       <td style={{ fontWeight: "700", verticalAlign: "middle" }}>{num.friendlyName || num.phoneNumber}</td>
                       <td style={{ fontWeight: "700", verticalAlign: "middle" }}>{num.fee}</td>
                       <td className="text-end" style={{ paddingRight: "25px" }}>
                         <Button
                           variant="primary"
                           size="sm"
                           disabled={purchasingId !== null}
                           onClick={() => handlePurchase(num)}
                           style={{
                             fontWeight: "700",
                             padding: "6px 16px",
                             backgroundColor: "#004992",
                             border: "none",
                             borderRadius: "6px",
                           }}
                         >
                           {purchasingId === num.phoneNumber ? <Spinner animation="border" size="sm" /> : "Buy Now"}
                         </Button>
                       </td>
                     </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default PhoneNumbers;
